// The window and level box, top right over an X-ray, the minimap's twin in the
// other corner, folded away and resized from a corner as it is (user,
// 2026-10-16): a close-up of the histogram three window-widths around the
// level, the entire range as a strip under it, and the window in the file's
// own numbers, and a row of its own for Auto (the window that shows the
// specimen best, windowing autoWindow), Copy, Paste (onto another X-ray) and
// Reset (the device's window, which it opens on).
//
// The histogram is a smooth curve filled with the grey each value is shown as
// — black below the window, the ramp across it, white above — so the window
// reads off the picture itself. The window is drawn in both the close-up and
// the strip, and taken by either: from its middle it moves (the level), by an
// edge it resizes (the black or the white point). The close-up holds still
// while its window is dragged, so the window follows the pointer; held at one
// of its sides a moving window scrolls it and an edge stretches it. It stays
// as a drag leaves it while the window still fits, and otherwise glides to
// frame the window afresh. Dragging on the image itself is
// the toolbar's tool (WindowDrag).

import { useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import { Check, ChevronLeft, ChevronRight, ClipboardPaste, Copy, RotateCcw, WandSparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ViewportEngine } from '../../viewer/ViewportEngine';
import {
  autoWindow,
  boundsOf,
  closeUpOf,
  histogramColumns,
  rangeOf,
  readoutParts,
  sourceWindow,
  storedWindow,
  type Window,
} from '../../viewer/window/windowing';
import type { WindowLevel } from './useWindowLevel';
import { useWindowClipboard } from './windowClipboard';
import { WindowDrag } from './WindowDrag';

/**
 * A little wider than the minimap (OverviewMinimap BASE_W 140), so four named
 * buttons fit (user, 2026-10-16); its height from the image's shape, as the
 * minimap's is.
 */
export const BASE_W = 160;
export const MIN_H = 96;
export const MAX_H = 240;
/** How large the corner may make it, keeping its shape; never smaller than it opens, where four named buttons just fit. */
const MIN_SCALE = 1;
const MAX_W = 420;
const MAX_BOX_H = 360;
/** The line under the chart for the window's numbers, and how the chart is laid out. */
export const ROW = 18;
export const STRIP = 10;
export const GAP = 5;
// A column a pixel (cols, in the box), smoothed over a few: bins of 16 values make steps otherwise.
const SMOOTH_CLOSE = 2.5;
const SMOOTH_STRIP = 1.2;
/** Empty track each side of the strip, as a share of the image's range: its ends show as ends. */
const STRIP_PAD = 0.03;
/** How near an edge (px) takes the edge rather than the middle. */
const EDGE = 5;
/** A window narrower than this (px) is taken whole: too thin to tell its edges apart. */
const MIN_EDGES = 16;
/** Held this near a side of the close-up (px), or past it, a drag scrolls it that way. */
const SCROLL_ZONE = 12;
/** Close-up spans a second at the zone's outer edge; faster further out, up to the cap. */
const SCROLL_SPEED = 0.75;
const SCROLL_CAP = 4;

/**
 * How fast (close-up spans a second) a drag held `depth` px into a side's
 * zone, or past it, scrolls the close-up: gently at first, quickly far out.
 */
export function scrollSpeed(depth: number): number {
  if (!(depth > 0)) return 0;
  return SCROLL_SPEED * Math.min(SCROLL_CAP, (depth / SCROLL_ZONE) ** 2);
}

/** How far (px, signed: left negative) pointer x on a close-up `width` wide sits in a side's zone. */
export function scrollDepth(x: number, width: number): number {
  if (x < SCROLL_ZONE) return x - SCROLL_ZONE;
  if (x > width - SCROLL_ZONE) return x - (width - SCROLL_ZONE);
  return 0;
}

/**
 * The close-up a drag held `depth` px into a side (signed) makes of `frame`
 * over `dt` s. Moving, it scrolls that way. Resizing, an edge held at its own
 * side zooms the close-up out about `anchor`, the edge not held, which stays
 * where it is drawn: the window grows in view instead of sliding out of it.
 * Held at the other side, it does nothing.
 */
export function scrolled(frame: [number, number], grip: WindowGrip, depth: number, dt: number, anchor = frame[0]): [number, number] {
  const rate = Math.sign(depth) * scrollSpeed(Math.abs(depth)) * dt;
  if (rate === 0) return frame;
  if (grip === 'move') {
    const d = rate * (frame[1] - frame[0]);
    return [frame[0] + d, frame[1] + d];
  }
  if ((grip === 'upper' && rate > 0) || (grip === 'lower' && rate < 0)) {
    const k = 1 + Math.abs(rate);
    return [anchor - (anchor - frame[0]) * k, anchor + (frame[1] - anchor) * k];
  }
  return frame;
}

/**
 * Whether a close-up still frames a window well: all of it inside, and a sixth
 * of it or wider, so it is taken by its edges with room to spare (MIN_EDGES).
 * While it does, the close-up stays as a drag left it; once it does not, it
 * frames the window afresh.
 */
export function framesWell(frame: [number, number], win: Window): boolean {
  const span = frame[1] - frame[0];
  // A drag held at a side leaves its edge a hair past it.
  const slack = span * 0.01;
  return win.center - win.width / 2 >= frame[0] - slack && win.center + win.width / 2 <= frame[1] + slack && win.width >= span / 6;
}

/** A re-framed close-up glides there, rather than jumping. */
const GLIDE_MS = 180;

/** `target`, eased to over GLIDE_MS each time it moves; at once while `instant` (a drag on it). */
function useGlide(target: [number, number], instant: boolean): [number, number] {
  const [shown, setShown] = useState<[number, number]>(target);
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const [t0, t1] = target;
  useEffect(() => {
    const from = shownRef.current;
    if (instant || (from[0] === t0 && from[1] === t1)) {
      setShown([t0, t1]);
      return;
    }
    let raf = 0;
    const began = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, Math.max(0, (now - began) / GLIDE_MS));
      const e = 1 - (1 - k) ** 3;
      setShown([from[0] + (t0 - from[0]) * e, from[1] + (t1 - from[1]) * e]);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [t0, t1, instant]);
  return instant ? target : shown;
}
const BLUE = '#3b82f6';

/** What a press on the box takes: the window's middle or one of its edges. */
export type WindowGrip = 'move' | 'lower' | 'upper';

/** Which part of a window drawn from x `a` to `b` a press at `x` takes, if any. */
export function gripAt(x: number, a: number, b: number): WindowGrip | null {
  if (b - a < MIN_EDGES) return Math.abs(x - (a + b) / 2) <= MIN_EDGES / 2 ? 'move' : null;
  if (Math.abs(x - a) <= EDGE) return 'lower';
  if (Math.abs(x - b) <= EDGE) return 'upper';
  return x > a && x < b ? 'move' : null;
}

/** The window once its `grip` has been dragged `dv` stored values, within `bounds`. */
export function gripped(start: Window, grip: WindowGrip, dv: number, [b0, b1]: [number, number] = [-Infinity, Infinity]): Window {
  const lower = start.center - start.width / 2;
  const upper = start.center + start.width / 2;
  // As on a slider: no edge past the ends of `bounds`, nor past the other edge.
  if (grip === 'move') {
    const l = Math.min(Math.max(lower + dv, b0), b1 - start.width);
    return { center: l + start.width / 2, width: start.width };
  }
  if (grip === 'lower') {
    const l = Math.min(Math.max(lower + dv, b0), upper - 1);
    return { center: (l + upper) / 2, width: upper - l };
  }
  const u = Math.max(Math.min(upper + dv, b1), lower + 1);
  return { center: (lower + u) / 2, width: u - lower };
}

/**
 * A histogram as a smooth curve over `width` × `height`, blurred over `sigma`
 * columns and drawn through with Catmull-Rom curves: the area under it, and
 * its line. Counts on a log scale (the strip: a small object shows beside a
 * vast open field) or a square-root one (the close-up: a log scale lifts the
 * last few dozen pixels of a peak nearly to its top, so its flank stood like a
 * cliff at the brightest value, which read as cut off; user, 2026-10-16).
 * Columns not `inside` the image's values are nothing, whatever the blur
 * spilled into them: a curve past the last value showed values not there.
 */
export function curve(
  counts: number[],
  width: number,
  height: number,
  sigma: number,
  { scale = 'log', inside }: { scale?: 'log' | 'sqrt'; inside?: (i: number) => boolean } = {},
): { area: string; line: string } | null {
  const scaled = counts.map((c) => (scale === 'sqrt' ? Math.sqrt(Math.max(0, c)) : Math.log10(1 + Math.max(0, c))));
  const top = Math.max(0, ...scaled);
  if (!(top > 0) || counts.length < 2) return null;
  const v = scaled.map((c) => c / top);
  const r = Math.ceil(sigma * 2.5);
  const k = Array.from({ length: 2 * r + 1 }, (_, i) => Math.exp(-((i - r) ** 2) / (2 * sigma * sigma)));
  const smooth = v.map((_, i) => {
    let sum = 0;
    let weight = 0;
    for (let j = -r; j <= r; j++) {
      if (i + j < 0 || i + j >= v.length) continue;
      sum += v[i + j] * k[j + r];
      weight += k[j + r];
    }
    return inside && !inside(i) ? 0 : sum / weight;
  });
  const step = width / smooth.length;
  // A little headroom, so the peak is not cut by the frame.
  const pts = smooth.map((s, i) => [(i + 0.5) * step, height - s * height * 0.92] as const);
  const y = (n: number) => Math.min(height, Math.max(0, n));
  let line = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, y(p1[1] + (p2[1] - p0[1]) / 6)];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, y(p2[1] - (p3[1] - p1[1]) / 6)];
    line += `C${c1[0].toFixed(1)} ${c1[1].toFixed(1)} ${c2[0].toFixed(1)} ${c2[1].toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  const last = pts[pts.length - 1];
  const area = `M0 ${height}L0 ${pts[0][1].toFixed(1)}L${line.slice(1)}L${width} ${last[1].toFixed(1)}L${width} ${height}Z`;
  return { area, line };
}

const CHEVRON = 'inline-flex h-4 w-4 cursor-pointer items-center justify-center rounded-[3px] bg-black/55 text-white';
// Full size, as the toolbar's are, the name under the icon: three to a row at the minimap's width.
const FOOT_BUTTON =
  'flex cursor-pointer flex-col items-center justify-center gap-0.5 rounded py-1 text-[11px] leading-none text-white/70 transition-colors hover:bg-white/10 hover:text-white';
/** How long Copy shows its tick. */
const COPIED_MS = 1200;

export function WindowBox({ level, engine }: { level: WindowLevel; engine: ViewportEngine }) {
  const { windowing, window: win, inverted } = level;
  const [collapsed, setCollapsed] = useState(false);
  // The close-up a drag on it left (or is making): kept while it frames the
  // window well, so letting go changes nothing on screen.
  const [held, setHeld] = useState<[number, number] | null>(null);
  const [closeDrag, setCloseDrag] = useState(false);
  const [grip, setGrip] = useState<WindowGrip | null>(null);
  // The cursor says what a press would take; nothing to take, nothing said.
  const [hover, setHover] = useState<WindowGrip | null>(null);
  const endDrag = useRef<(() => void) | null>(null);
  useEffect(() => () => endDrag.current?.(), []);
  const svgRef = useRef<SVGSVGElement>(null);
  const ids = useId().replace(/:/g, '');
  // Its size, from the bottom-left corner as the minimap's is from its own (user, 2026-10-16).
  const [scale, setScale] = useState(1);
  const { copied, copy } = useWindowClipboard();
  // The window that shows the specimen best, from the stored histogram (windowing autoWindow).
  const auto = useMemo(() => (windowing.histogram ? autoWindow(windowing.histogram) : null), [windowing.histogram]);
  const [justCopied, setJustCopied] = useState(false);
  useEffect(() => {
    if (!justCopied) return;
    const t = setTimeout(() => setJustCopied(false), COPIED_MS);
    return () => clearTimeout(t);
  }, [justCopied]);
  const [lo, hi] = useGlide(
    held && (closeDrag || framesWell(held, win)) ? held : closeUpOf(win),
    closeDrag,
  );

  const baseH = Math.round(Math.min(MAX_H, Math.max(MIN_H, BASE_W / (windowing.aspect ?? 1))));
  const maxScale = Math.min(MAX_W / BASE_W, MAX_BOX_H / baseH);
  const W = Math.round(BASE_W * scale);
  const h = Math.round(baseH * scale);
  const cols = W;
  const chartH = h - ROW;
  const closeH = chartH - STRIP - GAP;
  const stripTop = closeH + GAP;
  const [rangeLo, rangeHi] = rangeOf(windowing);
  // The strip's track: the image's range, and a little empty track past each end.
  const pad = (rangeHi - rangeLo) * STRIP_PAD;
  const [trackLo, trackHi] = [rangeLo - pad, rangeHi + pad];
  const holds = (v: number) => v >= rangeLo && v < rangeHi;
  const hist = windowing.histogram;
  // The strip changes only with the image and the box's size: not counted again on every frame of a drag.
  const strip = useMemo(
    () =>
      hist &&
      curve(histogramColumns(hist, trackLo, trackHi, cols), cols, STRIP, SMOOTH_STRIP, {
        inside: (i) => {
          const v = trackLo + ((i + 0.5) * (trackHi - trackLo)) / cols;
          return v >= rangeLo && v < rangeHi;
        },
      }),
    [hist, trackLo, trackHi, rangeLo, rangeHi, cols],
  );

  if (collapsed) {
    return (
      <div className="absolute right-2 top-2 z-[5]">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Show window and level"
              onClick={() => setCollapsed(false)}
              className="inline-flex cursor-pointer items-center rounded-md border border-white/20 bg-black/60 px-1.5 py-1 text-white"
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">Show window and level</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  const onClose = (v: number) => ((v - lo) / (hi - lo)) * W;
  const onRange = (v: number) => ((Math.min(Math.max(v, trackLo), trackHi) - trackLo) / (trackHi - trackLo)) * W;
  const lower = win.center - win.width / 2;
  const upper = win.center + win.width / 2;
  const [xa, xb] = [onClose(lower), onClose(upper)];
  const [sa, sb] = [onRange(lower), onRange(upper)];
  const close =
    hist &&
    curve(histogramColumns(hist, lo, hi, cols), W, closeH, SMOOTH_CLOSE, {
      scale: 'sqrt',
      inside: (i) => holds(lo + ((i + 0.5) * (hi - lo)) / cols),
    });
  // The grey each value is shown as: the window's ramp, black and white past its ends.
  const [from, to] = inverted ? ['#fff', '#000'] : ['#000', '#fff'];
  const { width: wText, level: lText } = readoutParts(win, windowing, true);

  // Copy keeps the window as the file says it; Paste sets it on this X-ray,
  // whichever way up it is stored. Copy also puts the words on the clipboard.
  const onCopy = () => {
    copy(sourceWindow(win, windowing));
    setJustCopied(true);
    // The words as the box shows them, so what lands in a report is what was seen.
    navigator.clipboard?.writeText(`W ${wText} · L ${lText}`).catch(() => {});
  };
  const onAuto = () => {
    if (!auto) return;
    setHeld(null);
    level.set(auto);
  };
  const onPaste = () => {
    if (!copied) return;
    setHeld(null);
    level.set(storedWindow(copied, windowing));
  };
  const pasting = copied && readoutParts(storedWindow(copied, windowing), windowing, true);
  const pasteTip = pasting ? `Paste W ${pasting.width} · L ${pasting.level}` : 'Copy a window first, then paste it on any X-ray';

  // The corner: anchored top right, so left and down grow it, the larger of
  // the two as the minimap's corner does, its shape kept.
  const startResize = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const [sx, sy, s0] = [e.clientX, e.clientY, scale];
    const move = (ev: PointerEvent) => {
      const byX = (BASE_W * s0 - (ev.clientX - sx)) / (BASE_W * s0);
      const byY = (baseH * s0 + (ev.clientY - sy)) / (baseH * s0);
      setScale(Math.min(maxScale, Math.max(MIN_SCALE, s0 * Math.max(byX, byY))));
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      endDrag.current = null;
    };
    endDrag.current?.();
    endDrag.current = end;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  /** Where on the box a pointer is, and what it would take there. */
  const target = (clientX: number, clientY: number) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return null;
    const x = clientX - r.left;
    const y = clientY - r.top;
    if (y >= 0 && y < closeH) {
      const g = gripAt(x, xa, xb);
      return g && { grip: g, perPx: (hi - lo) / W, closeUp: true };
    }
    if (y >= stripTop - GAP / 2 && y <= chartH) {
      const g = gripAt(x, sa, sb);
      return g && { grip: g, perPx: (trackHi - trackLo) / W, closeUp: false };
    }
    return null;
  };

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    const t = target(e.clientX, e.clientY);
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    const start = level.window;
    const bounds = boundsOf(windowing, start);
    const sx = e.clientX;
    const left = e.currentTarget.getBoundingClientRect().left;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // capture unsupported: the window listeners still follow the drag
    }
    setGrip(t.grip);
    let x = e.clientX;
    let raf = 0;
    let move: (ev: PointerEvent) => void;
    if (t.closeUp) {
      // The close-up holds still, so the window follows the pointer. Held at a
      // side, or past it, a moving window scrolls the close-up that way and an
      // edge stretches its own side, so one drag goes as far as wanted (user,
      // 2026-10-16); the close-up stays as it is left.
      let f: [number, number] = [lo, hi];
      // A moving window stays whole in view, its grip held back from the
      // sides by as much window as lies beyond it; an edge goes to the side.
      const px = e.clientX - left;
      const [minPx, maxPx] = t.grip === 'move' && xb - xa < W ? [px - xa, W - (xb - px)] : [0, W];
      const valueIn = (frame: [number, number], clientX: number) =>
        frame[0] + (Math.min(Math.max(clientX - left, minPx), maxPx) / W) * (frame[1] - frame[0]);
      const grabbed = valueIn(f, e.clientX);
      const windowIn = (frame: [number, number]) => gripped(start, t.grip, valueIn(frame, x) - grabbed, bounds);
      const apply = () => level.set(windowIn(f));
      // The edge not held, which a stretch keeps where it is drawn.
      const anchor = t.grip === 'upper' ? start.center - start.width / 2 : start.center + start.width / 2;
      setHeld(f);
      setCloseDrag(true);
      let then = performance.now();
      const tick = (now: number) => {
        const dt = Math.min(0.05, Math.max(0, (now - then) / 1000));
        then = now;
        const next = scrolled(f, t.grip, scrollDepth(x - left, W), dt, anchor);
        // At an end of the range the window goes no further, and nor does the close-up.
        const [here, there] = [windowIn(f), windowIn(next)];
        if (next !== f && (Math.abs(here.center - there.center) > 1e-6 || Math.abs(here.width - there.width) > 1e-6)) {
          f = next;
          setHeld(f);
          apply();
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      move = (ev) => {
        x = ev.clientX;
        apply();
      };
    } else {
      // On the strip the entire range is in view: the close-up follows the window.
      setHeld(null);
      move = (ev) => level.set(gripped(start, t.grip, (ev.clientX - sx) * t.perPx, bounds));
    }
    const end = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      endDrag.current = null;
      setCloseDrag(false);
      setGrip(null);
    };
    endDrag.current?.();
    endDrag.current = end;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };
  const shown = grip ?? hover;
  const cursor = shown === 'move' ? (grip ? 'grabbing' : 'grab') : shown ? 'ew-resize' : 'default';
  // A plain scroll zooms the image, as it does beside the box; a pinch is the page's.
  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    if (e.ctrlKey) return;
    engine.scrollZoom(e.nativeEvent, { x: e.clientX, y: e.clientY });
  };
  const edge = (x: number, active: boolean) => (
    <g>
      <line x1={x} y1={0} x2={x} y2={closeH} stroke={BLUE} strokeWidth={active ? 2 : 1.25} />
      <rect x={x - 2.5} y={closeH / 2 - 7} width={5} height={14} rx={2.5} fill={BLUE} stroke="rgba(0,0,0,0.5)" strokeWidth={0.75} />
    </g>
  );

  return (
    <div
      role="group"
      aria-label="Window and level"
      data-window-box
      className="absolute right-2 top-2 z-[5] touch-none select-none rounded-md border border-white/20 bg-black/60 p-[3px]"
      // As wide as its chart and no wider: sized to its content, "Copied" stretched it for a moment.
      style={{ width: W + 8 }}
      onWheel={onWheel}
    >
      <div className="relative overflow-hidden rounded">
        <svg
          ref={svgRef}
          data-window-chart
          width={W}
          height={chartH}
          className="block"
          style={{ cursor }}
          aria-hidden
          onPointerDown={onPointerDown}
          onPointerMove={(e) => !grip && setHover(target(e.clientX, e.clientY)?.grip ?? null)}
          onPointerLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={`ramp${ids}`} gradientUnits="userSpaceOnUse" x1={xa} x2={Math.max(xb, xa + 0.5)} y1={0} y2={0}>
              <stop offset={0} stopColor={from} />
              <stop offset={1} stopColor={to} />
            </linearGradient>
            <linearGradient id={`fade${ids}`} x1={0} x2={0} y1={0} y2={1}>
              <stop offset={0} stopColor="#fff" stopOpacity={1} />
              <stop offset={1} stopColor="#fff" stopOpacity={0.55} />
            </linearGradient>
            <mask id={`soft${ids}`}>
              <rect x={0} y={0} width={W} height={closeH} fill={`url(#fade${ids})`} />
            </mask>
          </defs>
          {/* The close-up: the window's span tinted, the curve filled with the grey each value becomes. */}
          <rect x={0} y={0} width={W} height={closeH} fill="rgba(255,255,255,0.04)" />
          <rect x={xa} y={0} width={Math.max(0, xb - xa)} height={closeH} fill="rgba(59,130,246,0.10)" />
          {close ? (
            <g mask={`url(#soft${ids})`}>
              <path d={close.area} fill={`url(#ramp${ids})`} />
              <path d={close.line} fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth={1} />
            </g>
          ) : (
            <rect x={0} y={closeH - 3} width={W} height={3} fill={`url(#ramp${ids})`} />
          )}
          {edge(xa, grip === 'lower')}
          {edge(xb, grip === 'upper')}
          {/* The strip: every value the image holds, the window as a pill on it. */}
          <g transform={`translate(0 ${stripTop})`}>
            <rect x={0} y={0} width={W} height={STRIP} rx={STRIP / 2} fill="rgba(255,255,255,0.07)" />
            {strip && <path d={strip.area} fill="rgba(255,255,255,0.35)" />}
            <rect
              x={Math.min(sa, W - 4) - 0.5}
              y={0.5}
              width={Math.max(4, sb - sa) + 1}
              height={STRIP - 1}
              rx={(STRIP - 1) / 2}
              fill="rgba(59,130,246,0.35)"
              stroke={BLUE}
              strokeWidth={1.25}
            />
          </g>
        </svg>
        {/* Hide: the minimap's chevron, mirrored for the other corner. */}
        <div className="absolute left-0.5 top-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Hide window and level" onClick={() => setCollapsed(true)} className={CHEVRON}>
                <ChevronRight className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">Hide window and level</TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="flex items-center justify-center px-1 text-[11px] tabular-nums" style={{ height: ROW }}>
        <span className="whitespace-nowrap text-white">
          <span className="text-white/50">W</span> {wText}
          <span className="ml-2 text-white/50">L</span> {lText}
        </span>
      </div>
      {/* Its own row, the buttons full size, each named (user, 2026-10-16). */}
      <div className={`mt-0.5 grid ${auto ? 'grid-cols-4' : 'grid-cols-3'} gap-0.5 border-t border-white/10 pt-0.5`}>
        {/* Only where there is a histogram to work from: an X-ray converted before histograms has none. */}
        {auto && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Auto window and level" onClick={onAuto} className={FOOT_BUTTON}>
                <WandSparkles className="h-4 w-4" />
                <span>Auto</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">The window that shows the specimen best</TooltipContent>
          </Tooltip>
        )}
        <button type="button" aria-label="Copy window and level" onClick={onCopy} className={FOOT_BUTTON}>
          {justCopied ? <Check className="h-4 w-4 text-white" /> : <Copy className="h-4 w-4" />}
          <span>{justCopied ? 'Copied' : 'Copy'}</span>
        </button>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Paste window and level"
              aria-disabled={!copied}
              onClick={onPaste}
              className={`${FOOT_BUTTON} ${copied ? '' : 'cursor-default opacity-40 hover:bg-transparent hover:text-white/70'}`}
            >
              <ClipboardPaste className="h-4 w-4" />
              <span>Paste</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{pasteTip}</TooltipContent>
        </Tooltip>
        <button
          type="button"
          aria-label="Reset the window"
          onClick={() => {
            // Starting over: the close-up frames the device's window afresh too.
            setHeld(null);
            level.reset();
          }}
          className={FOOT_BUTTON}
        >
          <RotateCcw className="h-4 w-4" />
          <span>Reset</span>
        </button>
      </div>
      {/* Resize: the minimap's corner, mirrored to the bottom left. */}
      <div
        aria-hidden
        data-window-box-resize
        onPointerDown={startResize}
        className="absolute bottom-0 left-0 h-2.5 w-2.5 cursor-nesw-resize"
        style={{
          background:
            'linear-gradient(225deg, transparent 0 50%, rgba(255,255,255,0.85) 50% 60%, transparent 60% 70%, rgba(255,255,255,0.85) 70% 80%, transparent 80%)',
        }}
      />
    </div>
  );
}

/**
 * An X-ray's window over its image: the box, and while the toolbar's tool is
 * armed the drag on the image (WindowDrag). The single view's, each compare
 * pane's, and the stack's.
 */
export function WindowOverlay({ level, engine, armed }: { level: WindowLevel; engine: ViewportEngine; armed: boolean }) {
  return (
    <>
      <WindowBox level={level} engine={engine} />
      {armed && <WindowDrag level={level} engine={engine} />}
    </>
  );
}
